import { useEffect, useMemo, useState } from 'react';
import { performAction } from '../api/sophia';

interface AdminForecastDashboardProps {
  threadId: string;
  messageId: string;
}

type ForecastStatsForm = {
  seasonId: string;
  pipelineRunIdx: string;
  shopIds: string[];
  gender: string;
};

type StartForecastForm = {
  season: string;
  onlyMasterOrder: boolean;
  overwrite: boolean;
  shopIds: string[];
  productIds: string;
  gender: string;
  modelName: string;
};

type PipelineRunInfo = {
  pipeline_run_idx: number;
  status?: string | null;
  state?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
};

type ForecastStatsCountByRun = {
  pipeline_run_idx: number | null;
  forecasted_product_shop_count: number;
};

type ForecastStatsSummary = {
  season_id?: number | string | null;
  shop_ids?: string[] | null;
  gender?: string | null;
  sku_count?: number | null;
  shop_count?: number | null;
  total_product_shop_count?: number | null;
  counts_by_pipeline_run: ForecastStatsCountByRun[];
};

type ForecastCoverageBar = {
  pipeline_run_idx: number | null;
  forecasted_product_shop_count: number;
  total_product_shop_count: number;
  season_id?: number | string | null;
  shop_ids?: string[] | null;
  gender?: string | null;
  sku_count?: number | null;
  shop_count?: number | null;
};

const FORECAST_RUN_COLORS = [
  'bg-sky-300',
  'bg-amber-300',
  'bg-emerald-300',
  'bg-rose-300',
  'bg-violet-300',
  'bg-cyan-300',
  'bg-lime-300',
  'bg-orange-300',
];

type ForecastJobStatus = {
  job_id: string;
  status?: string | null;
  season?: number | string | null;
  only_master_order?: boolean | null;
  overwrite?: boolean | null;
  shop_ids?: string[] | null;
  product_ids?: string[] | null;
  gender?: string | null;
  model_name?: string | null;
};

type ShopOption = {
  label: string;
  code: string;
};

type ShopSelectorKind = 'forecast-stats' | 'start-forecast';

const trimString = (value: unknown): string => String(value ?? '').trim();

const splitListInput = (value: string): string[] =>
  value
    .split(/[,\n;]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const toObject = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const parseJsonString = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const collectItems = (value: unknown, matcher: (item: unknown) => boolean): unknown[] => {
  const normalizedValue = parseJsonString(value);
  if (Array.isArray(normalizedValue)) return normalizedValue;
  const objectValue = toObject(normalizedValue);
  if (!objectValue) return [];
  if (matcher(objectValue)) return [objectValue];

  const preferredKeys = ['items', 'rows', 'results', 'data', 'entries'];
  for (const key of preferredKeys) {
    if (!(key in objectValue)) continue;
    const nested = collectItems(objectValue[key], matcher);
    if (nested.length > 0) return nested;
  }

  const values = Object.values(objectValue);
  const matchedValues = values.filter((item) => matcher(parseJsonString(item)));
  if (matchedValues.length > 0) return matchedValues;

  for (const item of values) {
    const nested = collectItems(item, matcher);
    if (nested.length > 0) return nested;
  }

  return [];
};

const extractActionPayload = (value: unknown, key: string): unknown => {
  const normalizedValue = parseJsonString(value);
  const root = toObject(normalizedValue);
  if (!root) return value;
  if (key in root) return parseJsonString(root[key]);
  if ('data' in root) {
    const normalizedData = parseJsonString(root.data);
    const dataObj = toObject(normalizedData);
    if (dataObj && key in dataObj) return parseJsonString(dataObj[key]);
  }
  return normalizedValue;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const normalizePipelineRun = (value: unknown): PipelineRunInfo | null => {
  const row = toObject(value);
  if (!row) return null;
  const pipelineRunIdx = toNumber(row.pipeline_run_idx);
  if (pipelineRunIdx === null) return null;
  return {
    pipeline_run_idx: pipelineRunIdx,
    status: toNullableString(row.status),
    state: toNullableString(row.state),
    started_at: toNullableString(row.started_at),
    completed_at: toNullableString(row.completed_at),
  };
};

const normalizeForecastStatsSummary = (value: unknown): ForecastStatsSummary | null => {
  const row = toObject(value);
  if (!row) return null;
  const totalProductShopCount = toNumber(row.total_product_shop_count);
  const countsByPipelineRunRaw = Array.isArray(row.counts_by_pipeline_run) ? row.counts_by_pipeline_run : [];
  const countsByPipelineRun = countsByPipelineRunRaw
    .map((entry) => {
      const item = toObject(entry);
      if (!item) return null;
      return {
        pipeline_run_idx: toNumber(item.pipeline_run_idx),
        forecasted_product_shop_count: toNumber(item.forecasted_product_shop_count) ?? 0,
      };
    })
    .filter((entry): entry is ForecastStatsCountByRun => entry !== null);

  if (totalProductShopCount === null && countsByPipelineRun.length === 0) return null;
  return {
    season_id: row.season_id as number | string | null | undefined,
    shop_ids: Array.isArray(row.shop_ids) ? row.shop_ids.map((item) => String(item)) : null,
    gender: toNullableString(row.gender),
    sku_count: toNumber(row.sku_count),
    shop_count: toNumber(row.shop_count),
    total_product_shop_count: totalProductShopCount,
    counts_by_pipeline_run: countsByPipelineRun,
  };
};

const normalizeForecastJobStatus = (value: unknown): ForecastJobStatus | null => {
  const row = toObject(value);
  if (!row) return null;
  const jobId = toNullableString(row.job_id);
  if (!jobId) return null;
  return {
    job_id: jobId,
    status: toNullableString(row.status),
    season: row.season as number | string | null | undefined,
    only_master_order: typeof row.only_master_order === 'boolean' ? row.only_master_order : null,
    overwrite: typeof row.overwrite === 'boolean' ? row.overwrite : null,
    shop_ids: Array.isArray(row.shop_ids) ? row.shop_ids.map((item) => String(item)) : null,
    product_ids: Array.isArray(row.product_ids) ? row.product_ids.map((item) => String(item)) : null,
    gender: toNullableString(row.gender),
    model_name: toNullableString(row.model_name),
  };
};

const normalizePipelineRunsPayload = (value: unknown): PipelineRunInfo[] =>
  collectItems(
    extractActionPayload(value, 'admin-pipeline-run-idx'),
    (item) => toNumber(toObject(parseJsonString(item))?.pipeline_run_idx) !== null,
  )
    .map(normalizePipelineRun)
    .filter((row): row is PipelineRunInfo => row !== null)
    .sort((a, b) => b.pipeline_run_idx - a.pipeline_run_idx);

const normalizeForecastStatsPayload = (value: unknown): ForecastStatsSummary | null =>
  normalizeForecastStatsSummary(extractActionPayload(value, 'admin-forecast-stats'));

const normalizeForecastJobStatusesPayload = (value: unknown): ForecastJobStatus[] =>
  collectItems(
    extractActionPayload(value, 'admin-start-forecast-status'),
    (item) => Boolean(toNullableString(toObject(parseJsonString(item))?.job_id)),
  )
    .map(normalizeForecastJobStatus)
    .filter((row): row is ForecastJobStatus => row !== null)
    .sort((a, b) => b.job_id.localeCompare(a.job_id));

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('it-IT');
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('it-IT');
};

const getRunLabel = (pipelineRunIdx: number | null): string =>
  pipelineRunIdx === null ? 'No Run' : `Run ${pipelineRunIdx}`;

const getRunColorClass = (pipelineRunIdx: number | null): string => {
  if (pipelineRunIdx === null) return 'bg-gray-300';
  return FORECAST_RUN_COLORS[Math.abs(pipelineRunIdx) % FORECAST_RUN_COLORS.length];
};

const getStatusTone = (status: string | null | undefined): string => {
  switch ((status ?? '').toLowerCase()) {
    case 'complete':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'running':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const getJobStatusDotClass = (status: string | null | undefined): string => {
  switch ((status ?? '').toLowerCase()) {
    case 'running':
      return 'border-green-500 bg-green-100';
    case 'queued':
      return 'border-yellow-500 bg-yellow-100';
    case 'failed':
      return 'border-red-500 bg-red-100';
    case 'complete':
    case 'completed':
    case 'finished':
    case 'succeeded':
      return 'border-black bg-gray-200';
    default:
      return 'border-gray-400 bg-gray-100';
  }
};

const hasActiveForecastJobs = (jobs: ForecastJobStatus[]): boolean =>
  jobs.some((job) => {
    const status = (job.status ?? '').toLowerCase();
    return status === 'queued' || status === 'running';
  });

const normalizeShopOptionsPayload = (value: unknown): ShopOption[] => {
  const payload = extractActionPayload(value, 'global-page-data');
  const root = toObject(payload);
  const shopsList = Array.isArray(root?.shops_list) ? root.shops_list : [];
  const shopMapping = toObject(root?.['shop-mapping']) ?? {};

  return shopsList
    .map((item) => {
      const label = trimString(item);
      if (!label) return null;
      const mappedCode = trimString(shopMapping[label]);
      const code = mappedCode || trimString(label.split(' - ')[0]);
      if (!code) return null;
      return { label, code };
    })
    .filter((item): item is ShopOption => item !== null);
};

const normalizeSeasonsPayload = (value: unknown): string[] => {
  const payload = extractActionPayload(value, 'global-page-data');
  const root = toObject(payload);
  const seasonsList = Array.isArray(root?.seasons_list) ? root.seasons_list : [];
  return seasonsList.map((item) => trimString(item)).filter((item) => item.length > 0);
};

const normalizeModelsPayload = (value: unknown): string[] => {
  const payload = extractActionPayload(value, 'global-page-data');
  const root = toObject(payload);
  const modelList = Array.isArray(root?.model_list) ? root.model_list : [];
  return modelList.map((item) => trimString(item)).filter((item) => item.length > 0);
};

export default function AdminForecastDashboard({
  threadId,
  messageId,
}: AdminForecastDashboardProps) {
  const [forecastStatsForm, setForecastStatsForm] = useState<ForecastStatsForm>({
    seasonId: '',
    pipelineRunIdx: '',
    shopIds: [],
    gender: '',
  });
  const [startForecastForm, setStartForecastForm] = useState<StartForecastForm>({
    season: '',
    onlyMasterOrder: false,
    overwrite: false,
    shopIds: [],
    productIds: '',
    gender: '',
    modelName: 'gnn',
  });
  const [forecastStatsLoading, setForecastStatsLoading] = useState(false);
  const [startForecastLoading, setStartForecastLoading] = useState(false);
  const [forecastJobStatusesLoading, setForecastJobStatusesLoading] = useState(false);
  const [pipelineRunsLoading, setPipelineRunsLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [forecastStatsResult, setForecastStatsResult] = useState<unknown>(null);
  const [forecastJobStatusesResult, setForecastJobStatusesResult] = useState<unknown>(null);
  const [pipelineRunsResult, setPipelineRunsResult] = useState<unknown>(null);
  const [availableShops, setAvailableShops] = useState<ShopOption[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [shopSearchByKind, setShopSearchByKind] = useState<Record<ShopSelectorKind, string>>({
    'forecast-stats': '',
    'start-forecast': '',
  });
  const [forecastStatus, setForecastStatus] = useState<{ stats: string; start: string; pipelineRuns: string; jobs: string }>({
    stats: '',
    start: '',
    pipelineRuns: '',
    jobs: '',
  });

  const pipelineRuns = useMemo(
    () =>
      normalizePipelineRunsPayload(pipelineRunsResult).filter(
        (row) => (row.status ?? '').toLowerCase() === 'complete',
      ),
    [pipelineRunsResult],
  );
  const forecastStatsSummary = useMemo(() => normalizeForecastStatsPayload(forecastStatsResult), [forecastStatsResult]);
  const forecastJobStatuses = useMemo(
    () => normalizeForecastJobStatusesPayload(forecastJobStatusesResult),
    [forecastJobStatusesResult],
  );
  const visibleForecastCoverageBars = useMemo(() => {
    if (!forecastStatsSummary) return [];
    const totalProductShopCount = forecastStatsSummary.total_product_shop_count ?? 0;
    const selectedPipelineRunIdx = trimString(forecastStatsForm.pipelineRunIdx);
    const allBars = forecastStatsSummary.counts_by_pipeline_run.map((item) => ({
      pipeline_run_idx: item.pipeline_run_idx,
      forecasted_product_shop_count: item.forecasted_product_shop_count,
      total_product_shop_count: totalProductShopCount,
      season_id: forecastStatsSummary.season_id,
      shop_ids: forecastStatsSummary.shop_ids,
      gender: forecastStatsSummary.gender,
      sku_count: forecastStatsSummary.sku_count,
      shop_count: forecastStatsSummary.shop_count,
    }));
    if (!selectedPipelineRunIdx) return allBars;
    return allBars.filter((bar) => String(bar.pipeline_run_idx ?? '') === selectedPipelineRunIdx);
  }, [forecastStatsForm.pipelineRunIdx, forecastStatsSummary]);

  const loadPipelineRuns = async () => {
    if (!threadId || !messageId) {
      setForecastStatus((prev) => ({ ...prev, pipelineRuns: 'Thread o messageId non disponibili.' }));
      return;
    }

    setPipelineRunsLoading(true);
    setForecastStatus((prev) => ({ ...prev, pipelineRuns: '' }));
    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'get-pipeline-run-idx',
      });
      setPipelineRunsResult(result);
      setForecastStatus((prev) => ({ ...prev, pipelineRuns: 'Lista pipeline run caricata.' }));
    } catch (err) {
      setForecastStatus((prev) => ({
        ...prev,
        pipelineRuns: err instanceof Error ? err.message : 'Errore nel caricamento pipeline run.',
      }));
    } finally {
      setPipelineRunsLoading(false);
    }
  };

  useEffect(() => {
    void loadPipelineRuns();
  }, [threadId, messageId]);

  const loadFilterOptions = async () => {
    if (!threadId || !messageId) return;
    setOptionsLoading(true);
    try {
      const [shopsResult, seasonsResult, modelsResult] = await Promise.all([
        performAction({
          threadId,
          messageId,
          actionName: 'list-available-shops',
        }),
        performAction({
          threadId,
          messageId,
          actionName: 'available-seasons',
        }),
        performAction({
          threadId,
          messageId,
          actionName: 'available-models',
        }),
      ]);
      setAvailableShops(normalizeShopOptionsPayload(shopsResult));
      setAvailableSeasons(normalizeSeasonsPayload(seasonsResult));
      setAvailableModels(normalizeModelsPayload(modelsResult));
    } catch {
      setAvailableShops([]);
      setAvailableSeasons([]);
      setAvailableModels([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    void loadFilterOptions();
  }, [threadId, messageId]);

  const loadForecastJobStatuses = async (actionName: 'get-start-forecast-status' | 'poll-start-forecast-status' = 'get-start-forecast-status') => {
    if (!threadId || !messageId) {
      setForecastStatus((prev) => ({ ...prev, jobs: 'Thread o messageId non disponibili.' }));
      return;
    }

    setForecastJobStatusesLoading(true);
    setForecastStatus((prev) => ({ ...prev, jobs: '' }));
    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName,
      });
      setForecastJobStatusesResult(result);
    } catch (err) {
      setForecastStatus((prev) => ({
        ...prev,
        jobs: err instanceof Error ? err.message : 'Errore nel caricamento job forecast.',
      }));
    } finally {
      setForecastJobStatusesLoading(false);
    }
  };

  useEffect(() => {
    void loadForecastJobStatuses();
  }, [threadId, messageId]);

  useEffect(() => {
    if (!hasActiveForecastJobs(forecastJobStatuses)) return;
    const intervalId = window.setInterval(() => {
      void loadForecastJobStatuses('poll-start-forecast-status');
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [forecastJobStatuses, threadId, messageId]);

  const removeForecastJob = async (jobId: string) => {
    if (!threadId || !messageId) {
      setForecastStatus((prev) => ({ ...prev, jobs: 'Thread o messageId non disponibili.' }));
      return;
    }

    try {
      setForecastStatus((prev) => ({ ...prev, jobs: '' }));
      await performAction({
        threadId,
        messageId,
        actionName: 'remove-forecast-run-history',
        params: {
          'job-id-to-remove': [jobId],
        },
      });
      setForecastJobStatusesResult((prev) => {
        const currentJobs = normalizeForecastJobStatusesPayload(prev);
        return {
          'admin-start-forecast-status': currentJobs.filter((job) => job.job_id !== jobId),
        };
      });
    } catch (err) {
      setForecastStatus((prev) => ({
        ...prev,
        jobs: err instanceof Error ? err.message : 'Errore durante la rimozione del job forecast.',
      }));
    }
  };

  const loadForecastStats = async () => {
    if (!threadId || !messageId) {
      setForecastStatus((prev) => ({ ...prev, stats: 'Thread o messageId non disponibili.' }));
      return;
    }

    setForecastStatsLoading(true);
    setForecastStatus((prev) => ({ ...prev, stats: '' }));
    try {
      const params: Record<string, unknown> = {};
      const pipelineRunIdx = trimString(forecastStatsForm.pipelineRunIdx);
      if (pipelineRunIdx) {
        params['admin-pipeline-run-idx'] = pipelineRunIdx;
      }
      const seasonId = trimString(forecastStatsForm.seasonId);
      if (seasonId) {
        params['admin-season-id'] = seasonId;
      }
      const shopIds = forecastStatsForm.shopIds;
      if (shopIds.length > 0) {
        params['admin-shop-ids'] = shopIds;
      }
      const genderValue = trimString(forecastStatsForm.gender);
      if (genderValue) {
        params['admin-gender'] = genderValue;
      }

      const result = await performAction({
        threadId,
        messageId,
        actionName: 'get-forecasts-stats',
        params,
      });
      setForecastStatsResult(result);
      setForecastStatus((prev) => ({ ...prev, stats: 'Statistiche forecast caricate.' }));
    } catch (err) {
      setForecastStatus((prev) => ({
        ...prev,
        stats: err instanceof Error ? err.message : 'Errore nel caricamento statistiche forecast.',
      }));
    } finally {
      setForecastStatsLoading(false);
    }
  };

  const runForecast = async () => {
    if (!threadId || !messageId) {
      setForecastStatus((prev) => ({ ...prev, start: 'Thread o messageId non disponibili.' }));
      return;
    }
    if (!trimString(startForecastForm.season)) {
      setForecastStatus((prev) => ({ ...prev, start: 'Season obbligatoria.' }));
      return;
    }

    setStartForecastLoading(true);
    setForecastStatus((prev) => ({ ...prev, start: '' }));
    try {
      const params: Record<string, unknown> = {
        'admin-season': trimString(startForecastForm.season),
        'admin-only-master-order': startForecastForm.onlyMasterOrder,
        'admin-overwrite': startForecastForm.overwrite,
      };
      const shopIds = startForecastForm.shopIds;
      if (shopIds.length > 0) {
        params['admin-shop-ids'] = shopIds;
      }
      const productIds = splitListInput(startForecastForm.productIds);
      if (productIds.length > 0) {
        params['admin-product-ids'] = productIds;
      }
      const genderValue = trimString(startForecastForm.gender);
      if (genderValue) {
        params['admin-gender'] = genderValue;
      }
      const modelName = trimString(startForecastForm.modelName);
      if (modelName) {
        params['admin-model-name'] = modelName;
      }

      const result = await performAction({
        threadId,
        messageId,
        actionName: 'start-forecast',
        params,
      });
      void result;
      setForecastStatus((prev) => ({ ...prev, start: 'Forecast messo in coda.' }));
      void loadPipelineRuns();
      void loadForecastJobStatuses();
    } catch (err) {
      setForecastStatus((prev) => ({
        ...prev,
        start: err instanceof Error ? err.message : 'Errore durante l’avvio del forecast.',
      }));
    } finally {
      setStartForecastLoading(false);
    }
  };

  const toggleForecastStatsShop = (shopCode: string) => {
    setForecastStatsForm((prev) => ({
      ...prev,
      shopIds: prev.shopIds.includes(shopCode)
        ? prev.shopIds.filter((item) => item !== shopCode)
        : [...prev.shopIds, shopCode],
    }));
  };

  const toggleStartForecastShop = (shopCode: string) => {
    setStartForecastForm((prev) => ({
      ...prev,
      shopIds: prev.shopIds.includes(shopCode)
        ? prev.shopIds.filter((item) => item !== shopCode)
        : [...prev.shopIds, shopCode],
    }));
  };

  const renderShopSelector = (
    selectorKind: ShopSelectorKind,
    selectedShopIds: string[],
    toggleShop: (shopCode: string) => void,
  ) => {
    const searchValue = shopSearchByKind[selectorKind];
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filteredShops = normalizedSearch
      ? availableShops.filter((shop) => shop.label.toLowerCase().includes(normalizedSearch))
      : availableShops;

    return (
      <div className="space-y-2 rounded border border-gray-300 bg-white px-3 py-2">
        <input
          type="text"
          value={searchValue}
          onChange={(e) =>
            setShopSearchByKind((prev) => ({
              ...prev,
              [selectorKind]: e.target.value,
            }))
          }
          className="w-full border border-gray-300 px-3 py-2 text-sm"
          placeholder="Cerca negozio..."
        />
        <div className="max-h-40 overflow-auto">
          {optionsLoading ? (
            <div className="text-xs text-gray-500">Caricamento negozi...</div>
          ) : filteredShops.length === 0 ? (
            <div className="text-xs text-gray-500">Nessun negozio disponibile.</div>
          ) : (
            <div className="space-y-2">
              {filteredShops.map((shop) => (
                <label key={shop.code} className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedShopIds.includes(shop.code)}
                    onChange={() => toggleShop(shop.code)}
                    className="mt-0.5"
                  />
                  <span className="leading-5">{shop.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full w-full overflow-auto bg-white p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin - Forecast</h1>
            <p className="text-sm text-gray-600">Visualizzazione pipeline run, statistiche e avvio forecast</p>
          </div>
        </div>

        <section className="space-y-4 border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Pipeline Run Indexes</h2>
            </div>
            <button
              type="button"
              onClick={loadPipelineRuns}
              disabled={pipelineRunsLoading}
              className="px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              {pipelineRunsLoading ? 'Aggiornamento...' : 'Aggiorna lista'}
            </button>
          </div>
          {forecastStatus.pipelineRuns && (
            <div className={`text-xs ${forecastStatus.pipelineRuns.toLowerCase().includes('caricata') ? 'text-green-700' : 'text-red-600'}`}>
              {forecastStatus.pipelineRuns}
            </div>
          )}
          <div className="overflow-auto border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Run Idx</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">State</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Started At</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Completed At</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Azione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {pipelineRuns.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-sm text-gray-500">
                      Nessuna pipeline run disponibile.
                    </td>
                  </tr>
                ) : (
                  pipelineRuns.map((row) => (
                    <tr key={row.pipeline_run_idx}>
                      <td className="px-3 py-2 font-medium text-gray-900">{row.pipeline_run_idx}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusTone(row.status)}`}>
                          {row.status ?? '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{row.state ?? '-'}</td>
                      <td className="px-3 py-2 text-gray-700">{formatDateTime(row.started_at)}</td>
                      <td className="px-3 py-2 text-gray-700">{formatDateTime(row.completed_at)}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setForecastStatsForm((prev) => ({ ...prev, pipelineRunIdx: String(row.pipeline_run_idx) }))}
                          className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Usa questo run
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4 border border-gray-200 bg-white p-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Get Forecast Stats</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Season ID</span>
                <select
                  value={forecastStatsForm.seasonId}
                  onChange={(e) => setForecastStatsForm((prev) => ({ ...prev, seasonId: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Tutte le stagioni</option>
                  {availableSeasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Pipeline Run Idx</span>
                <select
                  value={forecastStatsForm.pipelineRunIdx}
                  onChange={(e) => setForecastStatsForm((prev) => ({ ...prev, pipelineRunIdx: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Tutte le pipeline run</option>
                  {pipelineRuns.map((row) => (
                    <option key={row.pipeline_run_idx} value={String(row.pipeline_run_idx)}>
                      {row.pipeline_run_idx} · {row.status ?? '-'} · {row.state ?? '-'}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Gender</span>
                <input
                  type="text"
                  value={forecastStatsForm.gender}
                  onChange={(e) => setForecastStatsForm((prev) => ({ ...prev, gender: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Donna / Uomo"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Shop IDs</span>
                {renderShopSelector('forecast-stats', forecastStatsForm.shopIds, toggleForecastStatsShop)}
              </label>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={loadForecastStats}
                disabled={forecastStatsLoading}
                className="px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {forecastStatsLoading ? 'Caricamento...' : 'Carica Statistiche'}
              </button>
              {forecastStatus.stats && (
                <span className={`text-xs ${forecastStatus.stats.toLowerCase().includes('caricate') ? 'text-green-700' : 'text-red-600'}`}>
                  {forecastStatus.stats}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4 border border-gray-200 bg-white p-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Start Forecast</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Season</span>
                <select
                  value={startForecastForm.season}
                  onChange={(e) => setStartForecastForm((prev) => ({ ...prev, season: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Seleziona stagione</option>
                  {availableSeasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Gender</span>
                <input
                  type="text"
                  value={startForecastForm.gender}
                  onChange={(e) => setStartForecastForm((prev) => ({ ...prev, gender: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Donna / Uomo"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Shop IDs</span>
                {renderShopSelector('start-forecast', startForecastForm.shopIds, toggleStartForecastShop)}
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Product IDs</span>
                <textarea
                  value={startForecastForm.productIds}
                  onChange={(e) => setStartForecastForm((prev) => ({ ...prev, productIds: e.target.value }))}
                  className="min-h-[72px] w-full border border-gray-300 px-3 py-2 text-sm"
                  placeholder="product1, product2"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-gray-700">Model Name</span>
                <select
                  value={startForecastForm.modelName}
                  onChange={(e) => setStartForecastForm((prev) => ({ ...prev, modelName: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Seleziona modello</option>
                  {availableModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={startForecastForm.onlyMasterOrder}
                  onChange={(e) => setStartForecastForm((prev) => ({ ...prev, onlyMasterOrder: e.target.checked }))}
                />
                Only master order
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={startForecastForm.overwrite}
                  onChange={(e) => setStartForecastForm((prev) => ({ ...prev, overwrite: e.target.checked }))}
                />
                Overwrite
              </label>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={runForecast}
                disabled={startForecastLoading}
                className="px-4 py-2 bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {startForecastLoading ? 'Avvio...' : 'Avvia Forecast'}
              </button>
              {forecastStatus.start && (
                <span className={`text-xs ${forecastStatus.start.toLowerCase().includes('coda') ? 'text-green-700' : 'text-red-600'}`}>
                  {forecastStatus.start}
                </span>
              )}
            </div>
            <div className="space-y-3 border-t border-gray-200 pt-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">Job Queue</h3>
              {forecastStatus.jobs && (
                <div className="text-xs text-red-600">{forecastStatus.jobs}</div>
              )}
              {forecastJobStatuses.length === 0 ? (
                <div className="rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-xs text-gray-500">
                  Nessun job forecast presente in coda.
                </div>
              ) : (
                <div className="space-y-3">
                  {forecastJobStatuses.map((job) => {
                    const infoParts = [
                      job.season != null ? `season ${job.season}` : null,
                      job.gender ? `gender ${job.gender}` : null,
                      job.model_name ? `model ${job.model_name}` : null,
                      job.shop_ids && job.shop_ids.length > 0 ? `shops ${job.shop_ids.join(', ')}` : null,
                      job.product_ids && job.product_ids.length > 0 ? `products ${job.product_ids.join(', ')}` : null,
                      job.only_master_order != null ? `master ${job.only_master_order ? 'yes' : 'no'}` : null,
                      job.overwrite != null ? `overwrite ${job.overwrite ? 'yes' : 'no'}` : null,
                    ].filter((item): item is string => Boolean(item));

                    return (
                      <div key={job.job_id} className="flex items-start gap-4 rounded border border-gray-200 bg-gray-50 px-3 py-3">
                        <span
                          aria-hidden="true"
                          className={`mt-0.5 h-8 w-8 shrink-0 rounded-full border-2 ${getJobStatusDotClass(job.status)}`}
                        />
                        <div className="min-w-0 flex-1 text-sm text-gray-900">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-semibold">{job.job_id}</span>
                            <span className="text-gray-500">.</span>
                            <span className="font-medium capitalize">{job.status ?? 'unknown'}</span>
                            {infoParts.length > 0 && <span className="text-gray-500">.</span>}
                            {infoParts.length > 0 && (
                              <span className="min-w-0 whitespace-normal break-words text-gray-600">
                                {infoParts.join(' · ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => void removeForecastJob(job.job_id)}
                          className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                        >
                          Rimuovi
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="space-y-4 border border-gray-200 bg-white p-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Forecast Coverage</h2>
          </div>
          <div className="space-y-4">
            {visibleForecastCoverageBars.length === 0 || !forecastStatsSummary ? (
              <div className="border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                Carica le statistiche per visualizzare la copertura forecast.
              </div>
            ) : (
              <div className="space-y-4 border border-gray-200 bg-gray-50 p-4">
                {(() => {
                  const totalForecasted = visibleForecastCoverageBars.reduce(
                    (sum, row) => sum + row.forecasted_product_shop_count,
                    0,
                  );
                  const totalProductShopCount = forecastStatsSummary.total_product_shop_count ?? 0;
                  const totalPercentage =
                    totalProductShopCount > 0 ? (totalForecasted / totalProductShopCount) * 100 : 0;

                  return (
                    <>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  {visibleForecastCoverageBars.map((row) => (
                    <div key={`legend-${String(row.pipeline_run_idx)}`} className="flex items-center gap-2 text-xs text-gray-700">
                      <span className={`inline-block h-3 w-3 rounded-full border border-gray-400 ${getRunColorClass(row.pipeline_run_idx)}`} />
                      <span>{getRunLabel(row.pipeline_run_idx)}</span>
                      <span className="text-gray-500">
                        {formatNumber(row.forecasted_product_shop_count)} (
                        {totalProductShopCount > 0
                          ? `${((row.forecasted_product_shop_count / totalProductShopCount) * 100).toFixed(1)}%`
                          : '0.0%'}
                        )
                      </span>
                    </div>
                  ))}
                </div>
                <div className="h-7 overflow-hidden rounded-lg border-2 border-gray-800 bg-white">
                  <div className="flex h-full w-full">
                    {visibleForecastCoverageBars.map((row) => {
                      const width =
                        forecastStatsSummary.total_product_shop_count && forecastStatsSummary.total_product_shop_count > 0
                          ? `${(row.forecasted_product_shop_count / forecastStatsSummary.total_product_shop_count) * 100}%`
                          : '0%';
                      return (
                        <div
                          key={`segment-${String(row.pipeline_run_idx)}`}
                          className={`h-full border-r border-gray-700 last:border-r-0 ${getRunColorClass(row.pipeline_run_idx)}`}
                          style={{ width }}
                          title={`${getRunLabel(row.pipeline_run_idx)}: ${formatNumber(row.forecasted_product_shop_count)}`}
                        />
                      );
                    })}
                    <div className="h-full flex-1 bg-gray-100" />
                  </div>
                </div>
                <div className="text-right text-sm font-medium text-gray-800">
                  {formatNumber(totalForecasted)} / {formatNumber(totalProductShopCount)} ({totalPercentage.toFixed(1)}%)
                </div>
                <div className="grid gap-3 text-xs text-gray-700 sm:grid-cols-2 lg:grid-cols-4">
                  <div>Season: <span className="font-medium text-gray-900">{forecastStatsSummary.season_id ?? '-'}</span></div>
                  <div>Shop count: <span className="font-medium text-gray-900">{formatNumber(forecastStatsSummary.shop_count)}</span></div>
                  <div>SKU count: <span className="font-medium text-gray-900">{formatNumber(forecastStatsSummary.sku_count)}</span></div>
                  <div className="sm:col-span-2 lg:col-span-3">
                    Shops filter: <span className="font-medium text-gray-900">{forecastStatsSummary.shop_ids?.join(', ') || 'all'}</span>
                  </div>
                </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
